import React from 'react';
import type { AmendatoryLineItemProps } from '../AmendatoryCoverageLineItemFactory';
import CoverageOptionsWrapper from '../shared/CoverageItemsWrapper';
import CoverageButton from '../shared/CoverageButton';
import TickedSlider from '../../../../../../../../../agencies/quotes/PrimeX/customizeQuote/components/TickedSlider';
import {
  AdditionalInsuredModalConfig,
  type FormValues,
} from '../../../../../../../../primeX-underwriter/modals/coverageLineItemModals/AdditionalInsuredModal';
import { Modal } from '../../../../../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import type { AI } from '../../../../../../api/types/rater-x-preset';
import { useLocale } from '../../../../../../providers/LocaleProvider';
import useTypingTimeout from '../../../../hooks/useTypingTimeout';
import { Show } from '../../../../../../../../../../components/Show';
import { ContextTypes } from '../manager';

const AdditionalInsureds = (props: AmendatoryLineItemProps) => {
  const locale = useLocale();
  const _sliderTo = props.rules?.aiFactor?.to;
  const _sliderFrom = props.rules?.aiFactor?.from;

  const handleChange = useTypingTimeout((newValue: number | number[]) => {
    props.setCoverage({
      id: props.id,
      value: { props: { aiFactor: { value: newValue as number } } },
    });
  });

  const addAdditionalInsureds = (formValues: FormValues) => {
    const _ai = formatAIForState(formValues);

    props.setCoverage({
      id: props.id,
      value: {
        props: {
          additionalInsureds: { values: _ai as unknown as AI[] },
        },
      },
    });
  };

  return (
    <CoverageOptionsWrapper>
      <CoverageButton
        disabled={!props.isSelected}
        onClick={Modal.deferShow(AdditionalInsuredModalConfig, {
          data: {
            language: locale.language,
            onSubmit: addAdditionalInsureds,
            additionalInsureds: getDefaultAIFields(
              props.value?.props.additionalInsureds?.values as AI[]
            ),
          },
        })}
      >
        Add/Edit (
        {String(props.value?.props.additionalInsureds?.values?.length ?? 0)})
      </CoverageButton>

      <Show
        when={
          _sliderFrom != _sliderTo &&
          props.contextType === ContextTypes.UNDERWRITER
        }
      >
        <CoverageOptionsWrapper>
          <TickedSlider
            boxProps={{
              alignItems: 'center',
              gap: '1rem',
              width: '14rem',
              marginLeft: '1.5rem',
            }}
            onChange={handleChange.onChange}
            disabled={!props.isSelected}
            min={Number(_sliderFrom)}
            initialSliderValue={props.value?.props.aiFactor?.value as number}
            max={Number(_sliderTo)}
            step={props.rules?.aiFactor?.step}
          >
            {(value) => <>{value}</>}
          </TickedSlider>
        </CoverageOptionsWrapper>
      </Show>
    </CoverageOptionsWrapper>
  );
};

export default AdditionalInsureds;

export function getDefaultAIFields(values: AI[] | undefined) {
  if (!values) {
    return [];
  }

  return values.map((ai) => {
    return {
      businessName: ai.props.businessName.value ?? '',
      address1: ai.props.address1.value ?? '',
      address2: ai.props.address2?.value ?? '',
      address3: ai.props.address3?.value ?? '',
      city: ai.props.city.value ?? '',
      state: ai.props.state?.value ?? '',
      zipCode: ai.props.zipCode.value ?? '',
      naicsCode: ai.props.riskClassificationCode?.value ?? '',
      naicsDescription: ai.props.riskClassificationDescription?.value ?? '',
      sicCode: ai.props.riskClassificationCode?.value ?? '',
      sicDescription: ai.props.riskClassificationDescription?.value ?? '',
    };
  });
}

export function formatAIForState(formValues: FormValues) {
  return formValues.additionalInsureds?.map(
    ({ naicsCode, naicsDescription, ...rest }) => {
      return {
        props: Object.keys(rest).reduce(
          (acc, key) => {
            const value = rest[key as keyof typeof rest] ?? '';

            return {
              ...acc,
              [key]: { value },
            };
          },
          {
            riskClassificationCode: { value: naicsCode },
            riskClassificationDescription: { value: naicsDescription },
          } as unknown as AI
        ),
      };
    }
  );
}
